import { clamp } from '../core/math';
import type { BrushDynamics, BrushTip, TipImage } from './types';

/** Single-channel coverage bitmap, row 0 at the top. */
export interface CoverageBitmap {
  width: number;
  height: number;
  data: Uint8Array;
}

export function createCoverage(width: number, height: number): CoverageBitmap {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  return { width: w, height: h, data: new Uint8Array(w * h) };
}

/** Falloff of a circular tip, matching `brush.frag.glsl` so previews do not lie. */
export function circularCoverage(distance: number, inner: number): number {
  if (distance >= 1) return 0;
  if (distance <= inner) return 1;
  const t = (distance - inner) / Math.max(1e-6, 1 - inner);
  // smoothstep, the same curve the shader uses.
  return 1 - t * t * (3 - 2 * t);
}

/**
 * Where the falloff of a circular tip starts. Tiny brushes keep about a pixel of
 * antialiasing however hard they are set, or single-pixel strokes disappear.
 */
export function innerRadiusFor(hardness: number, radius: number): number {
  if (radius <= 1.5) return 0;
  return clamp(hardness, 0, 1 - 1.5 / radius);
}

/**
 * Rasterises one stamp of `tip` at `size` document pixels. The box is square so
 * that rotation never clips: the major axis is `size` and the minor one smaller.
 */
export function rasterizeTip(tip: BrushTip, size: number, image: TipImage | null): CoverageBitmap {
  const box = Math.max(1, Math.ceil(size));
  const out = createCoverage(box, box);
  const width = size;
  const height = size * clamp(tip.roundness, 0.05, 1);
  const radians = (tip.angle * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const centre = box / 2;
  const inner = innerRadiusFor(tip.hardness, width / 2);
  const sampled = tip.kind === 'pattern' ? image : null;

  for (let y = 0; y < box; y += 1) {
    for (let x = 0; x < box; x += 1) {
      // Pixel centre, rotated back into the tip's own frame.
      const dx = x + 0.5 - centre;
      const dy = y + 0.5 - centre;
      const lx = (dx * cos + dy * sin) / width;
      const ly = (-dx * sin + dy * cos) / height;
      let coverage = 0;
      if (sampled) {
        if (Math.abs(lx) <= 0.5 && Math.abs(ly) <= 0.5) {
          coverage = sampleImage(sampled, lx + 0.5, ly + 0.5);
        }
      } else {
        coverage = circularCoverage(Math.hypot(lx, ly) * 2, inner);
      }
      out.data[y * box + x] = Math.round(clamp(coverage, 0, 1) * 255);
    }
  }
  return out;
}

/** Bilinear sample of a tip image in 0..1 UV space. */
function sampleImage(image: TipImage, u: number, v: number): number {
  const x = clamp(u * image.width - 0.5, 0, image.width - 1);
  const y = clamp(v * image.height - 0.5, 0, image.height - 1);
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = Math.min(image.width - 1, x0 + 1);
  const y1 = Math.min(image.height - 1, y0 + 1);
  const fx = x - x0;
  const fy = y - y0;
  const top =
    image.data[y0 * image.width + x0] * (1 - fx) + image.data[y0 * image.width + x1] * fx;
  const bottom =
    image.data[y1 * image.width + x0] * (1 - fx) + image.data[y1 * image.width + x1] * fx;
  return (top * (1 - fy) + bottom * fy) / 255;
}

/**
 * Composites a stamp into a coverage buffer the way the GPU does: full flow
 * takes the maximum so a soft stroke has no darker seams, lower flow builds up.
 */
export function stampCoverage(
  destination: CoverageBitmap,
  stamp: CoverageBitmap,
  centreX: number,
  centreY: number,
  flow: number,
): void {
  const left = Math.round(centreX - stamp.width / 2);
  const top = Math.round(centreY - stamp.height / 2);
  const accumulate = flow < 1;

  for (let y = 0; y < stamp.height; y += 1) {
    const dy = top + y;
    if (dy < 0 || dy >= destination.height) continue;
    for (let x = 0; x < stamp.width; x += 1) {
      const dx = left + x;
      if (dx < 0 || dx >= destination.width) continue;
      const source = (stamp.data[y * stamp.width + x] / 255) * flow;
      if (source <= 0) continue;
      const index = dy * destination.width + dx;
      const current = destination.data[index] / 255;
      const next = accumulate ? source + current * (1 - source) : Math.max(current, source);
      destination.data[index] = Math.round(clamp(next, 0, 1) * 255);
    }
  }
}

/** Deterministic PRNG, so a preset thumbnail never changes between renders. */
export function makeRandom(seed: number): () => number {
  let state = seed >>> 0 || 1;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface StampPlacement {
  x: number;
  y: number;
  size: number;
  angle: number;
  roundness: number;
  flow: number;
}

/**
 * Applies the dynamics to one stamp. Kept free of GL so the same code drives the
 * painting engine and the panel preview.
 */
export function jitterStamp(
  base: { x: number; y: number; size: number; angle: number; roundness: number; flow: number },
  dynamics: BrushDynamics,
  direction: number,
  random: () => number,
): StampPlacement {
  const signed = (): number => random() * 2 - 1;
  const size = base.size * (1 - dynamics.sizeJitter * random());
  let angle = base.angle + signed() * dynamics.angleJitter * 180;
  if (dynamics.followDirection) angle += (direction * 180) / Math.PI;
  const roundness = clamp(base.roundness * (1 - dynamics.roundnessJitter * random()), 0.05, 1);
  const flow = clamp(base.flow * (1 - dynamics.flowJitter * random()), 0, 1);

  let { x, y } = base;
  if (dynamics.scatter > 0) {
    // Scatter is perpendicular to the stroke, which is how a spatter tip reads.
    const offset = signed() * dynamics.scatter * base.size;
    x += Math.cos(direction + Math.PI / 2) * offset;
    y += Math.sin(direction + Math.PI / 2) * offset;
  }
  return { x, y, size: Math.max(1, size), angle, roundness, flow };
}
